<template>
    <div class="www pt-2 pb-6 select-none">
        <div
            class="flex justify-end gap-2 rounded-t-md border border-b-0 border-gray-300 px-3 py-2"
        >
            <div class="grow pb-2 text-sm font-medium text-gray-700">
                {{ t('Placeholders') }}
            </div>

            <div class="flex-none">
                <ActionButton
                    v-tippy="t('buttonInsertAsImageCode')"
                    state="transparent"
                    :icon="MdiQrcodePlus"
                    :is-disabled="placeholders.length === 0 || !editorIsActive /*  || true */"
                    size="md"
                    class-override="px-px"
                    @click="
                        if (placeholders.length > 0 && editorIsActive) {
                            imageCodeDialogState.open = true;
                        }
                    "
                />
            </div>
            <div class="flex-none">
                <ActionButton
                    v-tippy="t('buttonInsertAsCondition')"
                    state="transparent"
                    :icon="MdiHeadQuestion"
                    :is-disabled="placeholders.length === 0 || !editorIsActive"
                    size="md"
                    class-override="px-px"
                    @click="
                        if (placeholders.length > 0 && editorIsActive) {
                            conditionDialogState.open = true;
                        }
                    "
                />
            </div>

            <Menu
                as="div"
                class="relative inline-block flex-none text-left"
            >
                <div>
                    <MenuButton
                        v-tippy="t('Placeholders')"
                        class="flex cursor-pointer items-center rounded-full px-px py-1 text-gray-400 hover:text-gray-600 focus:ring-0 focus:outline-hidden"
                    >
                        <span class="sr-only">{{ t('Placeholders') }}</span>
                        <MdiDotsVertical
                            class="h-5 w-5"
                            aria-hidden="true"
                        />
                    </MenuButton>
                </div>

                <Transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <MenuItems
                        class="absolute right-0 z-10 mt-2 max-h-[50vh] w-72 max-w-72 origin-top-right overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black/20 focus:outline-hidden lg:max-h-[60vh]"
                    >
                        <MenuItem
                            v-for="(placeholder, index) in placeholders"
                            :key="index"
                        >
                            <a
                                href="#"
                                :class="[
                                    index === selectedPlaceholderGroup
                                        ? 'bg-gray-100 text-gray-900'
                                        : 'text-gray-700 hover:bg-gray-100',
                                    'flex items-start justify-start gap-2 px-2 py-1 text-sm font-bold first:rounded-t-md last:rounded-b-md',
                                ]"
                                @click="selectedPlaceholderGroup = index"
                            >
                                <MdiCodeBraces
                                    v-if="placeholder.type === 'SIMPLE'"
                                    class="h-5 w-5 flex-none text-gray-400"
                                />
                                <MdiRepeat
                                    v-if="placeholder.type === 'MULTIPLE'"
                                    class="h-5 w-5 flex-none text-cyan-400"
                                />
                                <MdiQrcode
                                    v-if="placeholder.type === 'IAMGECODE'"
                                    class="h-5 w-5 flex-none text-amber-400"
                                />
                                <div class="flex flex-col gap-1">
                                    <span>{{ placeholder.name }}</span>
                                    <span
                                        v-if="(placeholder?.description || '').length > 0"
                                        class="text-xs font-normal text-gray-600"
                                        >{{ placeholder.description }}</span
                                    >
                                </div>
                            </a>
                        </MenuItem>
                    </MenuItems>
                </Transition>
            </Menu>
        </div>
        <div class="border border-t-0 border-b-0 border-gray-300 px-3 pb-2 text-sm shadow-sm">
            <strong>{{ placeholders[selectedPlaceholderGroup].name }}</strong> -
            {{ placeholders[selectedPlaceholderGroup].description }}
        </div>

        <div
            v-if="placeholders[selectedPlaceholderGroup].type === 'SIMPLE'"
            class="h-72 max-h-72 overflow-y-auto rounded-b-md border border-t-0 border-gray-300 p-2 sm:h-80 sm:max-h-80 md:h-96 md:max-h-96"
        >
            <div
                v-for="(variable, index) in placeholders[selectedPlaceholderGroup].model"
                :key="`${index}`"
                class="rounded-b-m"
            >
                <div class="mt-4 flex rounded-md shadow-sm">
                    <div class="flex grow items-stretch truncate">
                        <button
                            v-tippy="t('buttonInsertPlaceholder')"
                            type="button"
                            :class="[
                                !editorIsActive
                                    ? 'cursor-not-allowed text-gray-300'
                                    : 'text-gray-700 hover:bg-gray-50',
                                'focus:ring-none inline-flex cursor-pointer items-center space-x-2 rounded-l-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium focus:border-blue-500 focus:outline-hidden',
                            ]"
                            :disabled="!editorIsActive"
                            @click="
                                chooseConditionInsertMethod(
                                    createVariableMarkup(
                                        placeholders[selectedPlaceholderGroup].name,
                                        variable,
                                    ),
                                )
                            "
                        >
                            <MdiTextBoxPlus class="h-5 w-5" />
                        </button>
                        <div
                            :title="
                                extractRealDescription(
                                    placeholders[selectedPlaceholderGroup],
                                    variable,
                                )
                            "
                            :class="[
                                !editorIsActive
                                    ? 'bg-gray-100 text-gray-500'
                                    : 'bg-gray-50 text-gray-700',
                                'block w-full rounded-r-md border border-gray-300 px-3 py-2 text-xs shadow-sm sm:text-sm',
                            ]"
                        >
                            {{ variable }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="
                ['PDF', 'EMAIL'].includes(templateModel.type) &&
                placeholders[selectedPlaceholderGroup].type === 'MULTIPLE'
            "
            class="h-72 max-h-72 overflow-y-auto rounded-b-md border border-t-0 border-gray-300 p-2 py-6 sm:h-80 sm:max-h-80 md:h-96 md:max-h-96"
        >
            <div class="flex-none">
                <ActionButton
                    :value="t('buttonInsertAsTable')"
                    state="primary"
                    :icon="MdiTableSync"
                    :is-disabled="
                        placeholders.length === 0 ||
                        !editorIsActive ||
                        placeholders[selectedPlaceholderGroup].type !== 'MULTIPLE'
                    "
                    class-override="w-full"
                    @click="
                        if (
                            placeholders.length > 0 &&
                            editorIsActive &&
                            placeholders[selectedPlaceholderGroup].type === 'MULTIPLE'
                        ) {
                            iteratorDialogState.open = true;
                        }
                    "
                />
            </div>
            <div class="grid grid-cols-2 py-4">
                <ActionButton
                    :value="t('buttonInsertIteratorStart')"
                    state="warning"
                    :icon="MdiPlaylistPlay"
                    :is-disabled="
                        placeholders.length === 0 ||
                        !editorIsActive ||
                        placeholders[selectedPlaceholderGroup].type !== 'MULTIPLE'
                    "
                    class-override="rounded-none rounded-l-md border-r-gray-300"
                    @click="
                        if (
                            placeholders.length > 0 &&
                            editorIsActive &&
                            placeholders[selectedPlaceholderGroup].type === 'MULTIPLE'
                        ) {
                            iteratorInsertStart();
                        }
                    "
                />
                <ActionButton
                    :value="t('buttonInsertIteratorEnd')"
                    state="warning"
                    :icon="MdiPlaylistRemove"
                    :is-disabled="
                        placeholders.length === 0 ||
                        !editorIsActive ||
                        placeholders[selectedPlaceholderGroup].type !== 'MULTIPLE'
                    "
                    class-override="rounded-none rounded-r-md"
                    @click="
                        if (
                            placeholders.length > 0 &&
                            editorIsActive &&
                            placeholders[selectedPlaceholderGroup].type === 'MULTIPLE'
                        ) {
                            iteratorInsertEnd();
                        }
                    "
                />
            </div>
            <div
                v-for="(variable, index) in placeholders[selectedPlaceholderGroup].model"
                :key="`${index}`"
                class="rounded-b-m"
            >
                <div class="mt-4 flex rounded-md shadow-sm">
                    <div class="flex grow items-stretch truncate">
                        <button
                            v-tippy="t('buttonInsertPlaceholder')"
                            type="button"
                            :class="[
                                !editorIsActive
                                    ? 'cursor-not-allowed text-gray-300'
                                    : 'text-gray-700 hover:bg-blue-200',
                                'focus:ring-none inline-flex cursor-pointer items-center space-x-2 rounded-l-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium focus:border-blue-500 focus:outline-hidden',
                            ]"
                            :disabled="!editorIsActive"
                            @click="
                                chooseConditionInsertMethod(
                                    createIteratorVariableMarkup(
                                        placeholders[selectedPlaceholderGroup].name,
                                        variable,
                                    ),
                                )
                            "
                        >
                            <MdiTextBoxPlus class="h-5 w-5" />
                        </button>
                        <div
                            :title="
                                extractRealDescription(
                                    placeholders[selectedPlaceholderGroup],
                                    variable,
                                )
                            "
                            :class="[
                                !editorIsActive
                                    ? 'bg-gray-100 text-gray-500'
                                    : 'bg-gray-50 text-gray-700',
                                'block w-full rounded-r-md border border-gray-300 px-3 py-2 text-xs shadow-sm sm:text-sm',
                            ]"
                        >
                            {{ variable }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <ConditionEditor
        :placeholders="placeholders"
        :state="conditionDialogState"
    />
    <ImageCodeEditor :state="imageCodeDialogState" />
    <IteratorEditor
        :placeholders="placeholders"
        :state="iteratorDialogState"
    />
</template>

<script setup>
import { reactive, ref, watch, inject } from 'vue';
import {
    createVariableMarkup,
    createIteratorVariableMarkup,
    extractRealDescription,
} from '../composables/usePlaceholderTypes';
import { createBlock, insertBlock } from '../composables/useTemplateModel';
import { t } from '../composables/i18n';
import ConditionEditor from '../editor-widgets/ConditionEditorWidget.vue';
import ImageCodeEditor from '../editor-widgets/ImageCodeEditorWidget.vue';
import IteratorEditor from '../editor-widgets/IteratorEditorWidget.vue';
import ActionButton from '../ui/ActionButton.vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import {
    MdiTextBoxPlus,
    MdiHeadQuestion,
    MdiTableSync,
    MdiDotsVertical,
    MdiQrcodePlus,
    MdiPlaylistPlay,
    MdiPlaylistRemove,
    MdiCodeBraces,
    MdiRepeat,
    MdiQrcode,
} from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'PlaceholderWidget',
});

const props = defineProps({
    templateVersionModel: {
        type: Object,
        required: true,
    },
    templateModel: {
        type: Object,
        required: true,
    },
    templateEditModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    activeComponents: {
        type: Object,
        required: true,
    },
});

const templateVersionModel = props.templateVersionModel;
const templateModel = props.templateModel;
const templateEditModel = props.templateEditModel;
const activeComponents = props.activeComponents;
const placeholders = inject('placeholders');
const selectedPlaceholderGroup = ref(0);
const editorIsActive = ref(false);

const sortPlaceholdersByName = (a, b) => {
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
    }
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
    }
    return 0;
};

placeholders.sort(sortPlaceholdersByName);

const chooseConditionInsertMethod = (str) => {
    if (templateModel.type === 'SMS') {
        updateCursorPositionContent(str);
    }
    if (['PDF', 'EMAIL'].includes(templateModel.type)) {
        insertWYSIWYGEditorContent(str);
    }
};

const iteratorInsertStart = () => {
    const str = `<iterator-start-component class="loop-start">[#list ${placeholders[selectedPlaceholderGroup.value].name} as item]</iterator-start-component>`;
    insertWYSIWYGEditorContent(str);
};

const iteratorInsertEnd = () => {
    const str = '<iterator-end-component class="loop-end">[/#list]</iterator-end-component>';
    insertWYSIWYGEditorContent(str);
};

const insertWYSIWYGEditorContent = (str) => {
    activeComponents.editor.instance.commands.insertContent(str, {
        parseOptions: {
            updateSelection: true,
            preserveWhitespace: false,
        },
    });
    editorFocus(activeComponents.editor.instance, 0);
};

const iteratorBlockInsert = (str) => {
    const fixed = [
        {
            content: {
                tag: 'div',
                html: `${str}`,
                style: {},
            },
            style: {},
        },
    ];

    insertBlock(
        templateEditModel,
        activeComponents.content.rowId,
        createBlock(fixed, {
            placeholder: placeholders[selectedPlaceholderGroup.value].name,
            prefix: `[#list ${placeholders[selectedPlaceholderGroup.value].name} as item]`,
            suffix: '[/#list]',
            type: 'iterator',
        }),
        false,
    );
};

const conditionDialogState = reactive({
    open: false,
    action: chooseConditionInsertMethod,
    overlayPreventClose: true,
});

const imageCodeDialogState = reactive({
    open: false,
    action: chooseConditionInsertMethod,
    overlayPreventClose: true,
});

const iteratorDialogState = reactive({
    open: false,
    action: iteratorBlockInsert,
    selectedPlaceholderGroup: selectedPlaceholderGroup,
    overlayPreventClose: true,
});

const updateCursorPositionContent = (str) => {
    const elem = document.getElementById('sms-editor');
    let newValue = '';

    if (elem.selectionStart || elem.selectionStart == '0') {
        let _startSelection = elem.selectionStart;
        let _endSelection = elem.selectionEnd;
        newValue =
            elem.value.substring(0, _startSelection) +
            str +
            elem.value.substring(_endSelection, elem.value.length);
    } else {
        newValue += str;
    }
    templateVersionModel.source = newValue;
};

const editorFocus = (editor, left) => {
    setTimeout(function () {
        const pos = parseInt(editor.state.selection.$anchor.pos);
        editor.commands.focus(pos - left);
        editor.commands.scrollIntoView();
    }, 400);
};

/* watch(selectedPlaceholderGroup, () => {
    console.log(
        'WATCH selectedPlaceholderGroup',
        placeholders[selectedPlaceholderGroup.value],
    );
}); */

watch(activeComponents, () => {
    editorIsActive.value = activeComponents.methods.editorIsActive();
});
</script>
